import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useAppContext } from "./AppContext";
import {
  FeatureDiscoveryData,
  FeatureDiscoveryKey,
  GetUserDataResult,
  SavedHuiswerkSession,
  UserPreferences
} from "@/lib/types";
import appConfig from "@/config";
import { useRouter } from "next/navigation";
import { triggerLogout } from "@/lib/logoutevent";
import posthog from "posthog-js";
import { sendGTMEvent } from "@next/third-parties/google";
import { claimTiktokShareForCurrentUser } from "@/lib/tiktokShareAttribution";
import { PARENT_AUDIENCE_SESSION_KEY } from "@/lib/tiktokInviteRedirect";
import * as Sentry from "@sentry/nextjs";
import { signalAppReady } from "@/components/NativeSplashOverlay";
import { flushInfluencerAttribution } from "@/lib/influencerAttribution";
import {
  clearLocalTourPendingSync,
  isDismissedTourStatus,
  readLocalTourStatus,
  shouldSyncLocalTourStatus,
  type TourStatus
} from "@/lib/tourState";
import { getCheckoutRedirectPath } from "@/lib/authRedirect";

type AuthContext = {
  userData: GetUserDataResult | null | undefined;
  refreshUserData: () => Promise<GetUserDataResult | undefined>;
  devLogin: (email: string) => Promise<void>;
  isLoggedIn: boolean | null;
  isParent: boolean;
  isPremium: boolean;

  logout: () => Promise<void>;
  setHuiswerkSetOnUserData: (session: SavedHuiswerkSession | undefined) => void;
  setTourStatusOnUserData: (status: TourStatus) => void;
  setUserPreferencesOnUserData: (preferences: Partial<UserPreferences>) => void;
  setFeatureDiscoveryOnUserData: (
    featureKey: FeatureDiscoveryKey,
    discovery: FeatureDiscoveryData
  ) => void;
};

const AuthContext = createContext<AuthContext | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw Error("No AuthContext available");
  }
  return context;
};

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const { api, closeDrawer } = useAppContext();

  const [userData, setUserData] = useState<
    GetUserDataResult | null | undefined
  >(null);

  const router = useRouter();

  const refreshUserData = useCallback(async () => {
    try {
      const userData = await api.getUser();
      setUserData(userData);
      return userData;
    } catch {
      setUserData(undefined);
      return undefined;
    }
  }, [api]);

  const logout = useCallback(async () => {
    triggerLogout();

    if (posthogIdentifiedRef.current !== null) {
      posthog.reset();
      posthog.opt_out_capturing();
      posthogIdentifiedRef.current = null;
    }

    Sentry.setUser(null);
    setUserData(undefined);

    await api.logout();
  }, [router]);

  // Flush influencer attribution when a user completes authentication.
  // initialFetchDone ensures we only trigger on actual login/registration,
  // not on page load for already-logged-in users.
  const initialFetchDone = useRef(false);
  const prevUserData = useRef(userData);

  useEffect(() => {
    refreshUserData().finally(() => {
      initialFetchDone.current = true;
      signalAppReady();
    });
  }, []);
  useEffect(() => {
    if (
      initialFetchDone.current &&
      !prevUserData.current &&
      userData &&
      !userData.is_premium
    ) {
      flushInfluencerAttribution(api);
    }
    prevUserData.current = userData;
  }, [userData, api]);

  useEffect(() => {
    if (isDismissedTourStatus(userData?.tour_status)) {
      clearLocalTourPendingSync();
      return;
    }
    if (!shouldSyncLocalTourStatus(userData)) return;
    const localStatus = readLocalTourStatus();
    api
      .updateTourStatus(localStatus)
      .then(() => {
        clearLocalTourPendingSync();
        setUserData((prev) =>
          prev ? { ...prev, tour_status: localStatus } : prev
        );
      })
      .catch(() => {
        // Keep the local pending flag. A later authenticated page load can retry.
      });
  }, [api, userData?.email, userData?.tour_status]);

  const devLogin = useCallback(
    async (email: string) => {
      if (appConfig.environment === "development") {
        await api.devLogin(email); // Sets cookie for that dev-email
        await refreshUserData();
        const params = new URLSearchParams(window.location.search);
        const intent = params.get("intent");
        if (intent === "checkout") {
          router.replace(getCheckoutRedirectPath(params.get("token")));
        } else if (intent) {
          router.replace(intent);
        } else if (window.location.pathname.startsWith("/library/")) {
          router.replace(
            `${window.location.pathname}${window.location.search}`
          );
        } else {
          router.replace("/");
        }
      }
    },
    [api, refreshUserData, router]
  );

  const isLoggedIn = useMemo(() => {
    if (userData) return true;
    if (userData === undefined) return false;
    return null;
  }, [userData]);

  const isParent = useMemo(() => {
    return userData?.role === "parent";
  }, [userData]);

  const isPremium = useMemo(() => {
    return userData?.is_premium === true;
  }, [userData]);

  // Sync PostHog tracking mode with auth/premium state
  const posthogIdentifiedRef = useRef<string | null>(null);
  useEffect(() => {
    if (userData?.email) {
      Sentry.setUser({ email: userData.email });
    } else {
      Sentry.setUser(null);
    }

    if (userData?.email) {
      if (posthogIdentifiedRef.current !== userData.email) {
        posthog.opt_in_capturing();
        let audience: "parent" | "student" =
          userData.role === "parent" ? "parent" : "student";
        let entryFlow: string | null = null;
        try {
          if (sessionStorage.getItem(PARENT_AUDIENCE_SESSION_KEY) === "true") {
            audience = "parent";
          }
          entryFlow = sessionStorage.getItem("wiswiz_entry_flow");
        } catch {}
        const profielProps = userData.profiel
          ? {
              jaar: userData.profiel.jaar,
              niveau: userData.profiel.niveau,
              wiskunde_type: userData.profiel.wiskunde_type ?? null,
              methode: userData.profiel.methode
            }
          : {};
        posthog.identify(
          userData.email,
          {
            name: userData.full_name,
            plan: userData.is_premium ? "premium" : "free",
            audience,
            ...profielProps
          },
          {
            audience,
            ...(entryFlow ? { entry_flow: entryFlow } : {})
          }
        );
        posthogIdentifiedRef.current = userData.email;
        crypto.subtle
          ?.digest(
            "SHA-256",
            new TextEncoder().encode(userData.email.toLowerCase().trim())
          )
          .then((buf) => {
            const emailHash = Array.from(new Uint8Array(buf))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
            sendGTMEvent({ event: "user_identified", user_id: emailHash });
          })
          .catch(() => {});
        // TikTok demo flow: if a sharecode cookie is in scope, link this
        // parent to the share row so payment-time attribution can find it.
        // Backend filters non-parent roles; idempotent on repeat logins.
        void claimTiktokShareForCurrentUser();
      }
    } else if (posthogIdentifiedRef.current !== null) {
      posthog.stopSessionRecording();
      posthog.reset();
      posthog.opt_out_capturing();
      posthogIdentifiedRef.current = null;
    }
  }, [userData?.email, userData?.is_premium]);

  const setHuiswerkSetOnUserData = useCallback(
    (session: SavedHuiswerkSession | undefined) => {
      setUserData((prev) => {
        if (!prev) return prev;
        return { ...prev, huiswerk_set: session };
      });
    },
    []
  );

  const setTourStatusOnUserData = useCallback((status: TourStatus) => {
    setUserData((prev) => {
      if (!prev) return prev;
      return { ...prev, tour_status: status };
    });
  }, []);

  const setUserPreferencesOnUserData = useCallback(
    (preferences: Partial<UserPreferences>) => {
      setUserData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          user_preferences: {
            ...(prev.user_preferences ?? {}),
            ...preferences
          }
        };
      });
    },
    []
  );

  const setFeatureDiscoveryOnUserData = useCallback(
    (featureKey: FeatureDiscoveryKey, discovery: FeatureDiscoveryData) => {
      setUserData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          feature_discoveries: {
            ...(prev.feature_discoveries ?? {}),
            [featureKey]: discovery
          }
        };
      });
    },
    []
  );

  const providerValue = useMemo(
    () => ({
      userData,
      refreshUserData,
      devLogin,
      isLoggedIn,
      isPremium,
      isParent,
      logout,
      setHuiswerkSetOnUserData,
      setTourStatusOnUserData,
      setUserPreferencesOnUserData,
      setFeatureDiscoveryOnUserData
    }),
    [userData]
  );

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};
